import React, { useState } from 'react';
import classes from '../../../assets/Global.module.css';
import FinancialTable from '../../../helpers/financialsEditableTable/FinancialsTable';
import PostBody from '../../../api/internal/PostBody';
import View from '../../../helpers/slab/View';
import ViewCustomer from '../customer/Customer';
import ViewVendor from '../vendor/Vendor';
import AssembleUpdatedFinancial from '../../../helpers/financialsEditableTable/AssembleFinancialObject';
import ViewReceivable from '../receivable/Receivable';
import ViewPayable from '../payable/Payable';
import Button from '../../../assets/essentials/Button';
import Accordion from '../../../helpers/accordion/Accordion';
import BBITable from '../../../helpers/bBITable/BBITable';
import DateOnlyFormatter from '../../../helpers/inputs/DateOnlyFormatter';

interface RentalFinancialProps {
  data: RentalFinancials;
  rentalId: number;
  RefreshPage: Function;
}

function RentalFinancials({
  data,
  rentalId,
  RefreshPage,
}: RentalFinancialProps) {
  const [isPending, setIsPending] = useState<boolean>(false);

  function CreateAdditionalInvoice() {
    setIsPending(true);
    PostBody(`/rental/CreateRentalInvoice/${rentalId}`, null)
      .then(response => {
        if (response) {
          RefreshPage('Financials', ['Audit', 'Summary']);
        }
      })
      .finally(() => setIsPending(false));
  }

  const InvoicesSummary = () => {
    const totalReceivableFormatted = data.totalReceivable?.toLocaleString(
      'en-US',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    );

    const invoiceButtonText =
      data.customerFinancialsList.length > 0
        ? 'Create Additional Invoice'
        : 'Create Invoice';

    const ReceivableSection = ({
      customer,
    }: {
      customer: RentalCustomerAssignmentFinancialObject;
    }) => {
      function SaveDraftReceivable(e, assignmentId) {
        setIsPending(true);
        e.preventDefault();
        PostBody(
          'Accounting/SaveDraftFinancial',
          AssembleUpdatedFinancial(e, assignmentId, 'Customer'),
        ).then(response => {
          if (response) {
            RefreshPage('Financials', ['Audit', 'Summary']);
          }
        });
      }

      const ReceivableLinks = () => {
        return (
          <div className={classes.flexSpaceBetween}>
            <div className={classes.sideBySideButtons}>
              <div
                className={`${classes.cTA}`}
                onClick={() =>
                  View(
                    <ViewCustomer
                      customerId={customer.customerId}
                      customerName={customer.customerName}
                    />,
                  )
                }
              >
                View Customer
              </div>
              <div
                className={`${classes.cTA}`}
                onClick={() =>
                  View(
                    <ViewReceivable
                      customerAssignmentId={customer.customerAssignmentId}
                      loadId={null}
                      rentalId={rentalId}
                    />,
                  )
                }
              >
                View Receivable
              </div>
            </div>
            <div>Status: {customer.paymentStatus}</div>
          </div>
        );
      };

      return (
        <div key={customer.customerAssignmentId}>
          <div className={classes.flexColumn}>
            <h3 className={classes.headerWithoutMargin}>
              {customer.customerName}
            </h3>
            <ReceivableLinks />
          </div>
          <FinancialTable
            rowData={customer.receivables}
            assignmentId={customer.customerAssignmentId}
            assignmentType="Customer"
            accountTypeOptions={customer.receivableAccountTypeOptions}
            onSubmit={SaveDraftReceivable}
          />
        </div>
      );
    };

    const Receivables = ({ customerFinancialsList }) => {
      if (!customerFinancialsList) return null;
      return (
        <>
          {customerFinancialsList.map(
            (customer: RentalCustomerAssignmentFinancialObject) => (
              <ReceivableSection
                key={customer.customerAssignmentId}
                customer={customer}
              />
            ),
          )}
        </>
      );
    };

    return (
      <section className={classes.grayBottomDivider}>
        <Accordion
          defaultActive={data.customerFinancialsList.length > 0}
          title={
            <h2>Invoices - Total Receivable: ${totalReceivableFormatted}</h2>
          }
        >
          <Receivables customerFinancialsList={data.customerFinancialsList} />
        </Accordion>
        <div>
          <Button
            onClick={CreateAdditionalInvoice}
            disabled={isPending}
            variant="good"
          >
            {invoiceButtonText}
          </Button>
        </div>
      </section>
    );
  };

  const Bill = ({ bill }: { bill: RentalVendorAssignmentFinancialObject }) => {
    const PayableLinks = () => {
      return (
        <div className={classes.sideBySideButtons}>
          <div
            className={`${classes.cTA}`}
            onClick={() =>
              View(
                <ViewVendor
                  vendorId={bill.vendorId}
                  vendorName={bill.vendorName}
                />,
              )
            }
          >
            View Vendor
          </div>
          <div
            className={`${classes.cTA}`}
            onClick={() =>
              View(
                <ViewPayable
                  vendorAssignmentId={bill.vendorAssignmentId}
                  rentalId={rentalId}
                />,
              )
            }
          >
            View Payable
          </div>
        </div>
      );
    };

    const lineItemsColumnConfig = [
      {
        key: '2',
        attribute: 'chartOfAccountName',
        header: 'Account',
      },
      {
        key: '3',
        attribute: 'note',
        header: 'Note',
      },
      {
        key: '4',
        attribute: 'rate',
        header: 'Rate',
      },
      {
        key: '5',
        attribute: 'quantity',
        header: 'Quantity',
      },
      {
        key: '6',
        attribute: 'total',
        header: 'Total',
      },
    ];

    const formattedPayables = bill.payables.map(payable => ({
      ...payable,
      total: payable.total.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    }));

    return (
      <>
        <section key={bill.vendorAssignmentId}>
          <div
            className={`${classes.flexSpaceBetween} ${classes.marginBottom}`}
          >
            <div className={`${classes.flexColumn}`}>
              <h3 className={classes.headerWithoutMargin}>{bill.vendorName}</h3>
              <div className={classes.flexColumn}>
                <span>
                  Name: <strong>{bill.billName}</strong>
                </span>
                <span>
                  Date: <strong>{DateOnlyFormatter(bill.billDate)}</strong>
                </span>
                <span>
                  Status: <strong>{bill.paymentStatus}</strong>
                </span>
                <span>
                  QuickBooks: <strong>{bill.quickBooksBillId ?? ' - '}</strong>
                </span>
              </div>
              <PayableLinks />
            </div>
          </div>
          <BBITable data={formattedPayables} columns={lineItemsColumnConfig} />
        </section>
        <br />
      </>
    );
  };

  const BillsSummary = () => {
    const totalPayableFormatted = data.totalPayable?.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return (
      <section className={classes.grayBottomDivider}>
        <Accordion
          defaultActive={data.vendorFinancialsList.length > 0}
          title={<h2>Bills - Total Payable: ${totalPayableFormatted}</h2>}
        >
          {data.vendorFinancialsList
            .filter(bill => bill.payables && bill.payables.length > 0)
            .map(bill => (
              <Bill key={bill.vendorAssignmentId} bill={bill} />
            ))}
          <br />
        </Accordion>
      </section>
    );
  };

  const MarginSection = () => {
    const margin = data.totalReceivable - data.totalPayable;
    const marginFormatted = margin.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return (
      <section className={classes.grayBottomDivider}>
        <h2>Current Margin: ${marginFormatted}</h2>
      </section>
    );
  };

  return (
    <>
      <MarginSection />
      <InvoicesSummary />
      <BillsSummary />
    </>
  );
}

export default RentalFinancials;
